// components/Achievements.jsx
import React, { useEffect, useState } from "react";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PopoverInfo from "./PopoverInfo";
import { Modal } from "antd";

const Achievements = ({
  achievements,
  WorkLinks,
  Certifications,
  handleChange,
  setFieldValue,
  errors,
  isEnglish
}) => {
  const t = {
    infoBanner: isEnglish
      ? "🔍 Verify awards & projects | 🕒 ≥ 8 yrs experience or equivalent credentials | ✅ Approved upon verification"
      : "🔍 पुरस्कार और परियोजनाओं का सत्यापन | 🕒 ≥ 8 वर्ष का अनुभव या समकक्ष योग्यता | ✅ सत्यापन के बाद अनुमोदित",
    achievementsLabel: isEnglish ? "Achievements*" : "उपलब्धियां*",
    achievementsInfo: isEnglish
      ? "Mention notable performances, awards, or recognitions you’ve received."
      : "उल्लेखनीय प्रदर्शन, पुरस्कार या सम्मान का उल्लेख करें।",
    achievementsPlaceholder: isEnglish
      ? "List your major awards, notable projects, & recognitions"
      : "अपने प्रमुख पुरस्कार, उल्लेखनीय परियोजनाओं और मान्यताओं को सूचीबद्ध करें",
    workLinksLabel: isEnglish ? "Featured Work Links (optional)" : "प्रदर्शित कार्य लिंक (वैकल्पिक)",
    workLinksInfo: isEnglish
      ? "Share links to your top work—like YouTube videos, collaborations with known artists, or major recommendations."
      : "अपने बेहतरीन कार्यों के लिंक साझा करें—जैसे YouTube वीडियो, प्रसिद्ध कलाकारों के साथ सहयोग, या प्रमुख सिफारिशें।",
    workLinksHelper: isEnglish
      ? "Work links must be a valid URL (e.g., https://example.com)"
      : "कार्य लिंक एक वैध URL होना चाहिए (उदा., https://example.com)",
    addLinkBtn: isEnglish ? "Add" : "जोड़ें",
    addLinkLabel: isEnglish
      ? "+ Add Link"
      : "+ लिंक जोड़ें",
    addAnotherLinkLabel: isEnglish
      ? "+ Add Another Link"
      : "+ एक और लिंक जोड़ें",
    duplicateLinkAlert: isEnglish
      ? "URL already added"
      : "URL पहले से जोड़ा गया है",
    certificationLabel: isEnglish
      ? "Degrees/Certifications*"
      : "डिग्री/प्रमाण पत्र*",
    certificationHelper: isEnglish
      ? "Please select an image file (JPG, JPEG, or PNG only)."
      : "कृपया एक इमेज फ़ाइल चुनें (केवल JPG, JPEG या PNG)।",
    certificationInfo: isEnglish
      ? "Upload certificates received while performing, winning, or attending programs."
      : "प्रदर्शन, जीत या कार्यक्रमों में भाग लेने के दौरान प्राप्त प्रमाण पत्र अपलोड करें।",
    certificationFormats: isEnglish
      ? "Supported formats: JPG, JPEG, PNG"
      : "समर्थित प्रारूप: JPG, JPEG, PNG",
    addCertBtn: isEnglish ? "+ Add" : "+ जोड़ें",
    addMoreCertBtn: isEnglish ? "+ Add More" : "+ और जोड़ें",
  };

  const [linkInput, setLinkInput] = useState("");
  const [showLinkInput, setShowLinkInput] = useState(WorkLinks?.length < 1);
  const [textCount, setTextCount] = useState(0);
  const [currentImg, setCurrentImg] = useState("");
  const [isPreviewModal, setIsPreviewModal] = useState(false);

  const openPreviewModal = () => setIsPreviewModal(true);
  const closePreviewModal = () => setIsPreviewModal(false);

  useEffect(() => {
    setTextCount(achievements?.length || 0);
    setShowLinkInput(WorkLinks?.length < 1);
  }, [achievements, WorkLinks]);

  const handleAddLink = () => {
  if (!showLinkInput){ return setShowLinkInput(true);}
    if (WorkLinks.includes(linkInput)) {
      alert(t.duplicateLinkAlert);
      return;
    }
    setFieldValue("WorkLinks", [...WorkLinks, linkInput]);
    setLinkInput("");
    setShowLinkInput(false);
  };

  const handleDeleteLink = (url) => {
    setFieldValue(
      "WorkLinks",
      WorkLinks.filter((item) => item !== url)
    );
  };

  const pickCertification = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length < 1){ return; }
    const newFiles = [];

    files.forEach((file) => {
      const slug = file.name;
      const uri = URL.createObjectURL(file);
      const type = file.type;
      newFiles.push({ uri, slug, type, file});
    });

    const updated = [...(Certifications || []), ...newFiles];
    setFieldValue("Certifications", updated);
  };

  const handleDeleteImage = (uri) => {
    const updated = Certifications.filter((img) => img.uri !== uri);
    setFieldValue("Certifications", updated);
  };

  const inputBaseClass = `w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-[#0D1C2E] font-[Gilroy-Regular] focus:outline-none focus:ring focus:ring-blue-200`;

  return (
    <div className="flex flex-col gap-4">
      <div className="p-3 text-gray-200 bg-[#008080] text-sm rounded-xl mt-3">
        {t.infoBanner}
      </div>
      {/* Achievements Textarea */}
      <div>
        <label className="text-base font-medium text-[#0D1C2E] tracking-tight flex items-center gap-1">
          {t.achievementsLabel}
          <PopoverInfo infoText={t.achievementsInfo} />
        </label>
        <textarea
          rows={8}
          minLength={200}
          value={achievements}
          onChange={(e) => handleChange("achievements")(e)}
          className={inputBaseClass}
          placeholder={t.achievementsPlaceholder}
        />
        <div className="flex justify-between text-sm text-gray-500 mt-1">
          <span className="text-red-500">{errors.achievements}</span>
          <span style={{ color: textCount >= 200 ? "green" : "red" }} >{textCount} / 200 required</span>
        </div>
      </div>

      {/* Work Links */}
      <div>
        <label className="text-base font-medium text-[#0D1C2E] tracking-tight flex items-center gap-1">
          {t.workLinksLabel}
          <PopoverInfo infoText={t.workLinksInfo} />
        </label>
        <div className="text-xs text-amber-600 mt-1">{t.workLinksHelper}</div>

        {showLinkInput && (
          <div className="flex items-center gap-1 mt-2">
            <input
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              placeholder="https://example.com"
              className={`${inputBaseClass} flex-1`}
            />
            {linkInput && (
              <button
                onClick={handleAddLink}
                className="text-white font-medium tracking-tight px-4 py-2 bg-[#008080] rounded-lg transition-transform duration-150 active:scale-95"
              >
                {t.addLinkBtn}
              </button>
            )}
          </div>
        )}

        {WorkLinks?.map((url) => (
          <div
            key={url}
            className="flex items-center justify-between border border-gray-300 px-2 py-1 mt-2 rounded-lg"
          >
            <span className="truncate text-sm font-medium text-blue-600 w-4/5">{url}</span>
            <button
              onClick={() => handleDeleteLink(url)}
              className="bg-red-100 p-1 rounded-full"
            >
              <DeleteOutlineIcon className="size-3 text-[#D15D5D]" />
            </button>
          </div>
        ))}

        <div className="mt-2">
          {!linkInput && (
            <button
              onClick={handleAddLink}
              className="text-teal-600 text-base font-medium underline"
            >
              {WorkLinks.length < 1
                ? t.addLinkLabel
                : t.addAnotherLinkLabel}
            </button>
          )}
        </div>

        {errors.WorkLinks && (
          <div className="text-sm text-red-500">{errors.WorkLinks}</div>
        )}
      </div>

      {/* Certifications */}
      <div>
        <label className="text-base font-medium text-[#0D1C2E] tracking-tight flex items-center gap-1">
          {t.certificationLabel}
          <PopoverInfo infoText={t.certificationInfo} type="certification" />
        </label>
        <div className="text-xs text-amber-600 mt-1">{t.certificationHelper}</div>
        
        <div className="flex flex-wrap gap-2 mt-1">
          {Certifications?.length > 0 ? (
            Certifications.map((file) => {
              return (
                <div
                  key={file.uri}
                  onClick={() => {
                    setCurrentImg(file.uri);
                    openPreviewModal()
                  }}
                  className="relative min-w-[8rem] w-32 h-24 rounded-lg overflow-hidden border border-gray-300 shrink-0"
                >
                  <img className="h-full w-full object-cover" src={file.uri} />
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDeleteImage(file.uri)
                    }}
                    className="absolute top-1 right-1 bg-red-100 px-1 rounded-full"
                  >
                    <DeleteOutlineIcon className="size-4 text-[#D15D5D]" />
                  </button>
                </div>
              );
            })
          ) : (
            <label className="block border border-gray-300 p-3 py-2 w-full bg-white rounded-lg cursor-pointer text-sm text-gray-600 transition-transform duration-150 active:scale-95">
              {t.certificationFormats}
              <input
                type="file"
                accept="image/png, image/jpeg, image/jpg"
                onChange={pickCertification}
                className="hidden"
                multiple
              />
            </label>
          )}
        </div>

        <label className="text-teal-600 text-base font-medium mt-2 cursor-pointer block underline">
          <input
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            onChange={pickCertification}
            className="hidden"
            multiple
          />
          {Certifications?.length > 0
            ? t.addMoreCertBtn
            : t.addCertBtn}
        </label>

        {errors.Certifications && (
          <div className="text-sm text-red-500">
            {errors.Certifications}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {currentImg && (
        <Modal
          open={isPreviewModal}
          onCancel={closePreviewModal}
          footer={null}
          title="Degrees/Certifications"
          centered
          width="auto"
          styles={{
            padding: 14,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div className="max-h-[80vh] w-full flex justify-center items-center mt-2">
            <img
              src={currentImg}
              className="max-h-[80vh] w-auto rounded-md"
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Achievements;
